// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/LexicalEditor/Toolbar.js

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBold, faItalic, faUnderline, faImage,
  faListUl, faListOl, faLink, faCode, faQuoteRight, faAlignLeft, faAlignCenter, faAlignRight,
  faEraser, faTable
} from '@fortawesome/free-solid-svg-icons';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode, $createTextNode, $isRangeSelection, FORMAT_TEXT_COMMAND,
  $getSelection, FORMAT_ELEMENT_COMMAND, $isTextNode, $isElementNode, $getRoot
} from 'lexical';
import { $wrapNodes } from '@lexical/selection';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $createListNode, $createListItemNode } from '@lexical/list';
import { $createCodeNode } from '@lexical/code';
import { $createLinkNode, LinkNode } from '@lexical/link';
import { API_URLS } from '../../api/api';
import { $createImageNode } from '../Content/LexicalEditor';
import { INSERT_TABLE_COMMAND_CUSTOM } from './LexicalEditorTableUtils.jsx';

const Toolbar = ({ fieldName, onImageUpload, isUploading, setIsUploading }) => {
  const [editor] = useLexicalComposerContext();

  const formatText = (format) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
      }
    });
  };

  const clearFormatting = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const nodes = selection.getNodes();

      nodes.forEach((node) => {
        if ($isTextNode(node)) {
          node.setFormat(0);
        } else if (node.getType() === 'link') {
          const textContent = node.getTextContent();
          const textNode = $createTextNode(textContent);
          node.replace(textNode);
        } else if ($isElementNode(node)) {
          node.setFormat(0);
          node.setDirection(null);
        }
      });

      $wrapNodes(selection, () => {
        const paragraph = $createParagraphNode();
        paragraph.setFormat(0);
        paragraph.setDirection(null);
        return paragraph;
      });
    });
  };

  const formatElement = (alignment) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignment);
    });
  };

  const formatHeading = (headingType) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      if (headingType === '') {
        $wrapNodes(selection, () => $createParagraphNode());
      } else {
        $wrapNodes(selection, () => $createHeadingNode(headingType));
      }
    });
  };

  const formatList = (listType) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const nodes = selection.extract();
      const listNode = $createListNode(listType);

      if (!nodes.length) {
        const listItem = $createListItemNode();
        listItem.append($createTextNode(''));
        listNode.append(listItem);
        const paragraph = $createParagraphNode();
        paragraph.append(listNode);
        selection.insertNodes([paragraph]);
        return;
      }

      nodes.forEach((node) => {
        const listItem = $createListItemNode();
        node.remove();
        listItem.append(node);
        listNode.append(listItem);
      });

      if (listNode.getChildren().length > 0) {
        const paragraph = $createParagraphNode();
        paragraph.append(listNode);
        selection.insertNodes([paragraph]);
      }
    });
  };

  const formatQuote = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) $wrapNodes(selection, () => $createQuoteNode());
    });
  };

  const formatCode = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) $wrapNodes(selection, () => $createCodeNode());
    });
  };

  const formatLink = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const url = prompt('Enter the URL');
      if (!url) return;

      const selectedText = selection.getTextContent();
      const linkNode = $createLinkNode(url);

      if (selectedText) {
        const textNode = $createTextNode(selectedText);
        linkNode.append(textNode);
        selection.insertNodes([linkNode]);
      } else {
        selection.insertNodes([linkNode]);
      }
    });
  };

  const insertImageToEditor = (imageUrl) => {
    editor.update(() => {
      const selection = $getSelection();
      const imageNode = $createImageNode({ src: imageUrl, altText: 'Uploaded image' });
      if ($isRangeSelection(selection)) {
        selection.insertNodes([imageNode]);
      } else {
        const root = $getRoot();
        const paragraph = $createParagraphNode();
        paragraph.append(imageNode);
        root.append(paragraph);
      }
    });
  };

  const uploadImageToServer = async (file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    setIsUploading(true);
    try {
      const response = await fetch(API_URLS.IMAGE_UPLOAD, {
        method: 'POST',
        body: formDataUpload,
      });
      if (!response.ok) throw new Error(`Failed to upload image: ${response.statusText}`);
      const result = await response.json();
      if (!result.resizedFileURLs || !result.originalFileURLs) throw new Error('Invalid response format');
      return { resizedUrl: result.resizedFileURLs[0], originalUrl: result.originalFileURLs[0] };
    } catch (error) {
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file || !file.type.startsWith('image/')) {
        alert('Please select an image file (jpg/png)');
        return;
      }
      try {
        const { resizedUrl } = await uploadImageToServer(file);
        onImageUpload(fieldName, file, resizedUrl);
        insertImageToEditor(resizedUrl);
      } catch (error) {
        console.error('Image upload error:', error);
        alert(`Failed to upload image: ${error.message}`);
        onImageUpload(fieldName, null, '');
      }
    };
  };

  const handleInsertTable = () => {
    const rowsStr = prompt("Enter number of rows:", "3");
    const columnsStr = prompt("Enter number of columns:", "3");

    const rows = parseInt(rowsStr, 10);
    const columns = parseInt(columnsStr, 10);

    if (rows > 0 && columns > 0) {
      editor.dispatchCommand(INSERT_TABLE_COMMAND_CUSTOM, {
        rows: rows.toString(),
        columns: columns.toString(),
        includeHeaders: { rows: true, columns: true },
      });
    } else if (rowsStr || columnsStr) {
      alert("Invalid input. Please enter positive numbers for rows and columns.");
    }
  };

  return (
    <div className="editor-toolbar">
      <select onChange={(e) => formatHeading(e.target.value)} disabled={isUploading} className="btn btn-outline-secondary btn-sm me-1">
        <option value="">Normal</option>
        <option value="h1">H1</option>
        <option value="h2">H2</option>
        <option value="h3">H3</option>
        <option value="h4">H4</option>
        <option value="h5">H5</option>
        <option value="h6">H6</option>
      </select>
      <button type="button" className="btn btn-outline-secondary btn-sm me-1" onClick={() => formatText('bold')} disabled={isUploading}>
        <FontAwesomeIcon icon={faBold} />
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm me-1" onClick={() => formatText('italic')} disabled={isUploading}>
        <FontAwesomeIcon icon={faItalic} />
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm me-1" onClick={() => formatText('underline')} disabled={isUploading}>
        <FontAwesomeIcon icon={faUnderline} />
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm me-1" onClick={clearFormatting} disabled={isUploading}>
        <FontAwesomeIcon icon={faEraser} />
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm me-1" onClick={() => formatList('bullet')} disabled={isUploading}>
        <FontAwesomeIcon icon={faListUl} />
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm me-1" onClick={() => formatList('number')} disabled={isUploading}>
        <FontAwesomeIcon icon={faListOl} />
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm me-1" onClick={formatLink} disabled={isUploading}>
        <FontAwesomeIcon icon={faLink} />
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm me-1" onClick={formatCode} disabled={isUploading}>
        <FontAwesomeIcon icon={faCode} />
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm me-1" onClick={formatQuote} disabled={isUploading}>
        <FontAwesomeIcon icon={faQuoteRight} />
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm me-1" onClick={() => formatElement('left')} disabled={isUploading}>
        <FontAwesomeIcon icon={faAlignLeft} />
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm me-1" onClick={() => formatElement('center')} disabled={isUploading}>
        <FontAwesomeIcon icon={faAlignCenter} />
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm me-1" onClick={() => formatElement('right')} disabled={isUploading}>
        <FontAwesomeIcon icon={faAlignRight} />
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm me-1" onClick={handleInsertTable} disabled={isUploading}>
        <FontAwesomeIcon icon={faTable} />
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleImageUpload} disabled={isUploading}>
        <FontAwesomeIcon icon={faImage} /> {isUploading ? 'Uploading...' : 'Image'}
      </button>
    </div>
  );
};

export default Toolbar;